import { useState } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, Outlet, createFileRoute, useMatches, useParams } from "@tanstack/react-router";
import { formatDistanceToNow } from "date-fns";
import { ChevronLeft, Clock, FileText, X } from "lucide-react";
import { toast } from "sonner";

import { Skeleton } from "@/components/ui/skeleton";

import { useApi } from "@/api";
import {
  createOutcome,
  createTask,
  detachCanvas,
  getProject,
  getWorkPackage,
  getWorkPackageActivity,
  listProjectMembers,
  patchTask,
  patchWorkPackage,
} from "@/api/plan";
import { canEdit } from "@/components/chat/railMode";
import { AttachCanvasPicker } from "@/components/plan/AttachCanvasPicker";
import { DeskScreen } from "@/components/plan/DeskScreen";
import { LinkedIntegrations } from "@/components/plan/LinkedIntegrations";
import { ProgressBar } from "@/components/plan/ProgressBar";
import { StatusPill } from "@/components/plan/StatusPill";
import { humanizeStatus } from "@/components/plan/plan-status";
import { useDocumentTitle } from "@/lib/document-title";

export const Route = createFileRoute("/_app/plan/p/$projectSlug/wp/$wpId")({
  component: WorkPackageDetail,
});

// priority value → mockup .prio-badge modifier class (critical/high/medium/low).
const PRIO_CLS: Record<string, string> = {
  critical: "crit",
  high: "high",
  medium: "med",
  low: "low",
};
const PRIO_OPTIONS = ["critical", "high", "medium", "low"] as const;

// artifact status → mockup .art-status modifier + label.
const ART_STATE: Record<string, { label: string; cls: string }> = {
  signed_off: { label: "Signed Off", cls: "published" },
  draft: { label: "Draft", cls: "draft" },
};

// Activity `type` → mockup .act-ico glyph (the Act→Plan tag bridge included).
const ACT_ICON: Record<string, string> = {
  tag: "🏷",
  output: "🏷",
  edit: "✎",
  outcome: "◎",
  checkin: "◎",
  task: "✓",
  artifact: "◆",
};

// First letter of a user id → avatar initial (fallback for the owner sans name).
const ownerInitial = (id: string | null) => (id ? id.charAt(0).toUpperCase() : "?");
// Display name → up-to-two-letter avatar initials.
const initials = (n: string | null | undefined) =>
  (n || "?")
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0])
    .join("")
    .toUpperCase();
// "2 hours ago" relative timestamp for the activity feed.
const relAt = (iso: string) => {
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return "";
  }
};

// "2026-07-01" / ISO → "Jul 1"; pass-through anything that isn't a parseable date.
function fmtDue(raw: string): string {
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}
// A target date is overdue when it's in the past and the WP isn't finished.
function isOverdue(raw: string, health: string): boolean {
  if (health === "completed") return false;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d.getTime() < today.getTime();
}

/** Inline "type + Add / Cancel" composer styled with the mockup's .input + .btn. */
function InlineAddRow({
  placeholder,
  onSubmit,
  onCancel,
  pending,
  rowClassName,
}: {
  placeholder: string;
  onSubmit: (text: string) => void;
  onCancel: () => void;
  pending?: boolean;
  rowClassName: string;
}) {
  const [text, setText] = useState("");
  const submit = () => {
    const t = text.trim();
    if (t && !pending) {
      onSubmit(t);
      setText("");
    }
  };
  return (
    <div className={rowClassName}>
      <input
        className="input task-add-text"
        autoFocus
        placeholder={placeholder}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") submit();
          if (e.key === "Escape") onCancel();
        }}
      />
      <button className="btn primary sm" onClick={submit} disabled={!text.trim() || pending}>
        Add
      </button>
      <button className="btn sm" onClick={onCancel}>
        Cancel
      </button>
    </div>
  );
}

/** Task add row — text + an estimated-effort (hours) field (mockup task-add-row).
 * The hours feed the WP/project estimate roll-up. */
function TaskAddRow({
  onSubmit,
  onCancel,
  pending,
}: {
  onSubmit: (text: string, hours: number | null) => void;
  onCancel: () => void;
  pending?: boolean;
}) {
  const [text, setText] = useState("");
  const [hours, setHours] = useState("4");
  const submit = () => {
    const t = text.trim();
    if (!t || pending) return;
    const h = Number.parseFloat(hours);
    onSubmit(t, Number.isNaN(h) ? null : h);
  };
  return (
    <div className="task-add-row">
      <input
        className="input task-add-text"
        autoFocus
        placeholder="New task…"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") submit();
          if (e.key === "Escape") onCancel();
        }}
      />
      <label className="task-add-est" title="Estimated effort (hours)">
        <input
          className="input"
          type="number"
          min="0"
          step="1"
          value={hours}
          onChange={(e) => setHours(e.target.value)}
        />
        <span className="tae-unit">h</span>
      </label>
      <button className="btn primary sm" onClick={submit} disabled={!text.trim() || pending}>
        Add
      </button>
      <button className="btn sm" onClick={onCancel}>
        Cancel
      </button>
    </div>
  );
}

function WorkPackageDetail() {
  const { projectSlug, wpId } = useParams({
    from: "/_app/plan/p/$projectSlug/wp/$wpId",
  });
  const queryClient = useQueryClient();
  const key = ["plan", "wp", wpId];

  const [addingOutcome, setAddingOutcome] = useState(false);
  const [addingTaskFor, setAddingTaskFor] = useState<string | null>(null);
  const [attachOpen, setAttachOpen] = useState(false);
  const [editingPrio, setEditingPrio] = useState(false);
  // Activity section collapse state — supporting context, kept below the fold.
  const [actOpen, setActOpen] = useState(false);

  // The task-detail route nests under this one; when it's active, yield the
  // pane to its <Outlet/> (this route renders no Outlet otherwise).
  const matches = useMatches();
  const onTaskRoute = matches.some((m) => m.routeId.endsWith("/wp/$wpId/t/$taskId"));

  const api = useApi();
  const detail = useQuery({ queryKey: key, queryFn: () => getWorkPackage(wpId) });
  // Real WP activity feed (edits, completed tasks, tagged outputs).
  const activityQuery = useQuery({
    queryKey: ["plan", "wp-activity", wpId],
    queryFn: () => getWorkPackageActivity(wpId),
  });
  const activity = activityQuery.data ?? [];
  // The route is workspace-slug-free (`/plan/p/:projectSlug/wp/:wpId`), so the
  // workspace + project identity come from the WP detail payload.
  const workspaceId = detail.data?.work_package.workspace_id;
  const projectId = detail.data?.work_package.project_id;
  // Contributors = the parent project's members (the WP has no own roster). Drives
  // the avatar stack alongside the WP owner.
  const membersQuery = useQuery({
    queryKey: ["plan", "project-members", projectId],
    queryFn: () => listProjectMembers(projectId!),
    enabled: !!projectId,
  });
  // Resolve the workspace slug for the cross-mode Act canvas links
  // (`/w/:slug/c/:canvasId`) from the cached workspaces list.
  const workspaces = useQuery({
    queryKey: ["workspaces"],
    queryFn: () => api.listWorkspaces(),
  });
  const workspaceSlug = (
    workspaces.data as Array<{ id: string; slug: string }> | undefined
  )?.find((w) => w.id === workspaceId)?.slug;
  // Parent-project name for the "via <project>" suffix on contribution chips.
  const project = useQuery({
    queryKey: ["plan", "project", projectId],
    queryFn: () => getProject(projectId!),
    enabled: !!projectId,
  });
  const projectName = project.data?.project.name ?? "this project";
  // Canvas names for the attached list + the picker (shared cache).
  const canvasList = useQuery({
    queryKey: ["canvases", workspaceId],
    queryFn: () => api.listCanvases(workspaceId!),
    enabled: !!workspaceId,
  });
  const canvasNameById = new Map((canvasList.data ?? []).map((c) => [c.id, c.name]));

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: key });
    void queryClient.invalidateQueries({ queryKey: ["plan", "wp-activity", wpId] });
    void queryClient.invalidateQueries({ queryKey: ["plan", "project", projectId] });
    void queryClient.invalidateQueries({ queryKey: ["plan", "project-by-slug", projectSlug] });
    void queryClient.invalidateQueries({ queryKey: ["plan", "projects"] });
  };

  const addOutcome = useMutation({
    mutationFn: (text: string) => createOutcome(wpId, { text }),
    onSuccess: () => {
      setAddingOutcome(false);
      invalidate();
    },
    onError: (e) => toast.error((e as Error).message),
  });
  const addTask = useMutation({
    mutationFn: ({
      outcomeId,
      text,
      estimateHours,
    }: {
      outcomeId: string;
      text: string;
      estimateHours: number | null;
    }) => createTask(outcomeId, { text, estimate_hours: estimateHours }),
    onSuccess: () => {
      setAddingTaskFor(null);
      invalidate();
    },
    onError: (e) => toast.error((e as Error).message),
  });
  const toggleTask = useMutation({
    mutationFn: ({ taskId, done }: { taskId: string; done: boolean }) =>
      patchTask(taskId, { done }),
    onSuccess: invalidate,
    onError: (e) => toast.error((e as Error).message),
  });
  const detach = useMutation({
    mutationFn: (attachmentId: string) => detachCanvas(wpId, attachmentId),
    onSuccess: invalidate,
    onError: (e) => toast.error((e as Error).message),
  });
  // Optional, simple priority editor — a select that PATCHes work_package.priority.
  const setPriority = useMutation({
    mutationFn: (priority: string) => patchWorkPackage(wpId, { priority }),
    onSuccess: () => {
      setEditingPrio(false);
      invalidate();
    },
    onError: (e) => toast.error((e as Error).message),
  });

  // The task child route, when open, names the tab itself: it is deeper.
  useDocumentTitle([detail.data?.work_package.title ?? "Work package", project.data?.project.name]);

  if (onTaskRoute) return <Outlet />;

  const crumb = (
    <>
      <Link to="/plan/p/$projectSlug" params={{ projectSlug }} className="back">
        <ChevronLeft className="size-3.5" />
        {projectName}
      </Link>
      <div className="crumb">Work Package</div>
    </>
  );

  if (detail.isLoading) {
    return (
      <DeskScreen narrow>
        {crumb}
        <Skeleton className="h-8 w-64" />
        <Skeleton className="mt-4 h-32 w-full" />
      </DeskScreen>
    );
  }
  if (detail.isError || !detail.data) {
    return (
      <DeskScreen narrow>
        {crumb}
        <p className="text-[13px] text-[var(--color-semantic-error)]">
          {(detail.error as Error)?.message ?? "Work package not found."}
        </p>
      </DeskScreen>
    );
  }

  const {
    work_package: wp,
    outcomes,
    canvas_attachments,
    contributes_to,
    artifacts,
    health_label,
  } = detail.data;
  // Page-body write gate (editor+). Viewers/commenters see read-only content.
  const mayEdit = canEdit(detail.data.my_role);

  // Flat task list across all outcomes (mockup's separate Tasks section).
  const allTasks = outcomes.flatMap(({ outcome, tasks }) =>
    tasks.map((t) => ({ ...t, outcomeText: outcome.text })),
  );
  const tasksDone = allTasks.filter((t) => t.done).length;
  const firstOutcomeId = outcomes[0]?.outcome.id ?? null;

  const prioCls = wp.priority ? PRIO_CLS[wp.priority] ?? "med" : "med";
  const wpIndex =
    project.data?.work_packages?.findIndex((w) => w.work_package.id === wpId) ?? -1;
  const wpRef = wpIndex >= 0 ? `WP-${wpIndex + 1}` : null;

  // Contributors = the parent project's members. The WP owner leads the stack
  // (its real name from the roster if present, else its id initial); the other
  // members follow. With no roster available we show the owner alone.
  const members = membersQuery.data ?? [];
  const ownerMember = members.find((m) => m.user_id === wp.owner_user_id);
  const contributors = [
    {
      key: wp.owner_user_id ?? "owner",
      initial: ownerMember?.name
        ? initials(ownerMember.name)
        : ownerInitial(wp.owner_user_id),
      label: ownerMember?.name ?? ownerMember?.email ?? wp.owner_user_id ?? "Unassigned",
      owner: true,
    },
    ...members
      .filter((m) => m.user_id !== wp.owner_user_id)
      .map((m) => ({
        key: m.user_id,
        initial: initials(m.name ?? m.email),
        label: m.name ?? m.email ?? m.user_id,
        owner: false,
      })),
  ];

  // Header lives ABOVE the sheet (mockup "Canvas Is Sacred": identity + health on
  // the desk, the work itself on the paper).
  const toolbar = (
    <div className="desk-head">
      <Link to="/plan/p/$projectSlug" params={{ projectSlug }} className="back">
        ← {projectName}
      </Link>
      <div className="crumb">
        WORK PACKAGE
        {wpRef && (
          <>
            {" "}
            <span className="sep">·</span> {wpRef}
          </>
        )}
      </div>

      <div className="wpx-head">
        <div className="wpx-top">
          <StatusPill status={health_label} lg className="wp-status" />
        </div>
        <h1 className="page" style={{ margin: "0 0 12px" }}>
          {wp.title}
        </h1>
        {wp.description && <p className="wpx-desc">{wp.description}</p>}

        {/* Meta row — owner + contributor avatars · priority badge · due date (mockup .wpx-meta). */}
        <div className="wpx-meta">
          {/* Contributor avatar stack — the WP owner plus the parent project's members. */}
          <span className="avstack">
            {contributors.map((c) => (
              <span
                className={`mini-avatar sm${c.owner ? " owner" : ""}`}
                key={c.key}
                title={c.owner ? `Owner · ${c.label}` : c.label}
              >
                {c.initial}
              </span>
            ))}
          </span>
          <span className="wpx-meta-sep" />
          {mayEdit && editingPrio ? (
            <select
              className="input"
              style={{ padding: "3px 8px", fontSize: 12 }}
              autoFocus
              value={wp.priority ?? "medium"}
              onChange={(e) => setPriority.mutate(e.target.value)}
              onBlur={() => setEditingPrio(false)}
              disabled={setPriority.isPending}
            >
              {PRIO_OPTIONS.map((p) => (
                <option key={p} value={p}>
                  {humanizeStatus(p)}
                </option>
              ))}
            </select>
          ) : mayEdit ? (
            <button
              type="button"
              className={`prio-badge ${prioCls}`}
              title="Change priority"
              style={{ border: "none", cursor: "pointer" }}
              onClick={() => setEditingPrio(true)}
            >
              {wp.priority ? humanizeStatus(wp.priority) : "Set priority"}
            </button>
          ) : (
            wp.priority && <span className={`prio-badge ${prioCls}`}>{humanizeStatus(wp.priority)}</span>
          )}
          <span
            className={`wpx-due${wp.target_date && isOverdue(wp.target_date, health_label) ? " over" : ""}`}
          >
            Due {wp.target_date ? fmtDue(wp.target_date) : "—"}
          </span>
        </div>

        {/* Contributes to — KR chips inherited from the parent project (per-link weight). */}
        {contributes_to.length > 0 && (
          <div className="contributes-row">
            <span className="contributes-label">Contributes to</span>
            {contributes_to.map((c) => (
              <span
                key={c.kr_id}
                className="contributes-chip"
                title={`${c.kr_name} · ${c.weight}% · via ${projectName}`}
              >
                {c.kr_name} <span className="contributes-w">· {c.weight}%</span>
              </span>
            ))}
            <span className="contributes-via">via {projectName}</span>
          </div>
        )}

        <div className="wp-progress">
          <div className="wp-progress-head">
            <span className="wp-progress-l">Progress</span>
            <span className="wp-progress-pct">{wp.progress}%</span>
          </div>
          <ProgressBar value={wp.progress} variant="kr" className="!mt-0" />
        </div>
      </div>
    </div>
  );

  return (
    <DeskScreen toolbar={toolbar}>

      {/* Outcomes — flat list of rows inside one bordered .list (mockup).
          data-tour anchor: the demo guided tour spotlights this section as
          the "work defined ↔ work done" surface — outcome rows (the promise)
          interleaved with their tagged-output evidence sub-rows (the
          delivery, ◆-marked artifacts tagged from Act mode). */}
      <section className="section wp-section" data-tour="plan-outputs">
        <div className="wp-sec-head">
          <h2 className="sec" style={{ margin: 0 }}>
            Outcomes{" "}
            <span className="cell-dim" style={{ fontWeight: 400 }}>
              ({outcomes.length})
            </span>
          </h2>
          {mayEdit && (
            <button className="btn sm" onClick={() => setAddingOutcome((a) => !a)}>
              + Add outcome
            </button>
          )}
        </div>
        <div className="list">
          {outcomes.map(({ outcome, tasks, evidence }, i) => {
            const done = tasks.filter((t) => t.done).length;
            return (
              <div key={outcome.id}>
                <div className="oc-row">
                  <span className="oc-ico">◎</span>
                  <span className="oc-text">
                    <span className="ref-id">O-{i + 1}</span>
                    {outcome.text}
                  </span>
                  <span className="oc-tasks">
                    {done}/{tasks.length} task{tasks.length === 1 ? "" : "s"}
                  </span>
                  <StatusPill status={outcome.status} />
                </div>
                {/* Tagged outputs (the Act→Plan bridge), kept as muted sub-rows. */}
                {evidence.map((e) => (
                  <div
                    key={e.id}
                    className="oc-row"
                    style={{ paddingLeft: 26 }}
                    title={e.detail ?? undefined}
                  >
                    <span className="evidence-ico">◆</span>
                    <span className="oc-text" style={{ fontWeight: 400 }}>
                      {e.label}
                    </span>
                    {e.detail && <span className="oc-tasks">{e.detail}</span>}
                  </div>
                ))}
              </div>
            );
          })}
          {outcomes.length === 0 && !addingOutcome && (
            <div className="loading" style={{ padding: 16 }}>
              No outcomes yet.
            </div>
          )}
          {addingOutcome && (
            <InlineAddRow
              rowClassName="task-add-row"
              placeholder="New outcome — a promised result…"
              onSubmit={(t) => addOutcome.mutate(t)}
              onCancel={() => setAddingOutcome(false)}
              pending={addOutcome.isPending}
            />
          )}
        </div>
      </section>

      {/* Tasks — separate flat section spanning every outcome (mockup). */}
      <section className="section wp-section">
        <div className="wp-sec-head">
          <h2 className="sec" style={{ margin: 0 }}>
            Tasks{" "}
            <span className="cell-dim" style={{ fontWeight: 400 }}>
              ({tasksDone}/{allTasks.length})
            </span>
          </h2>
          {mayEdit && (
            <button
              className="btn sm"
              disabled={!firstOutcomeId}
              onClick={() => firstOutcomeId && setAddingTaskFor(firstOutcomeId)}
            >
              + Add task
            </button>
          )}
        </div>
        <div className="list">
          {allTasks.map((t, i) => (
            <div key={t.id} className="check-row task-row">
              {mayEdit ? (
                <button
                  type="button"
                  className="check-btn"
                  title={t.done ? "Mark incomplete" : "Mark complete"}
                  onClick={() => toggleTask.mutate({ taskId: t.id, done: !t.done })}
                >
                  <span className={`checkbox${t.done ? " on" : ""}`}>{t.done ? "✓" : ""}</span>
                </button>
              ) : (
                <span className="check-btn" aria-hidden>
                  <span className={`checkbox${t.done ? " on" : ""}`}>{t.done ? "✓" : ""}</span>
                </span>
              )}
              <Link
                to="/plan/p/$projectSlug/wp/$wpId/t/$taskId"
                params={{ projectSlug, wpId, taskId: t.id }}
                className={`check-label task-link min-w-0 flex-1 truncate${t.done ? " done" : ""}`}
              >
                <span className="ref-id">T-{i + 1}</span>
                {t.text}
              </Link>
              {(t.evidence_count ?? 0) > 0 && (
                <span
                  className="task-evidence"
                  title={`Completed by ${t.evidence_count} tagged artifact${
                    t.evidence_count === 1 ? "" : "s"
                  } in Work mode`}
                >
                  ◆ {t.evidence_count}
                </span>
              )}
              {(t.estimate_hours ?? 0) > 0 && (
                <span className="task-est" title="Estimated effort">
                  <Clock size={11} />
                  {t.estimate_hours}h
                </span>
              )}
            </div>
          ))}
          {allTasks.length === 0 && addingTaskFor === null && (
            <div className="loading" style={{ padding: 16 }}>
              No tasks yet.
            </div>
          )}
          {addingTaskFor !== null && (
            <TaskAddRow
              onSubmit={(text, hours) =>
                addTask.mutate({ outcomeId: addingTaskFor, text, estimateHours: hours })
              }
              onCancel={() => setAddingTaskFor(null)}
              pending={addTask.isPending}
            />
          )}
        </div>
      </section>

      {/* Canvases — the Act↔Plan bridge: real canvases attached to this WP. */}
      <section className="section wp-section">
        <div className="wp-sec-head">
          <h2 className="sec" style={{ margin: 0 }}>
            Canvases{" "}
            <span className="cell-dim" style={{ fontWeight: 400 }}>
              ({canvas_attachments.length})
            </span>
          </h2>
          {mayEdit && (
            <button className="btn sm" disabled={!workspaceId} onClick={() => setAttachOpen(true)}>
              + Attach canvas
            </button>
          )}
        </div>
        {canvas_attachments.length === 0 ? (
          <p className="sec-note">
            No canvases attached yet — link an Act-mode canvas to ground this work in real output.
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {canvas_attachments.map((a) => (
              <div
                key={a.id}
                className="check-row"
                style={{ border: "1px solid var(--color-hairline)", gap: 10 }}
              >
                <FileText className="size-3.5 shrink-0 text-[var(--color-steel)]" />
                <Link
                  to="/w/$slug/c/$canvasId"
                  params={{ slug: workspaceSlug ?? "", canvasId: a.canvas_id }}
                  className="check-label min-w-0 flex-1 truncate"
                >
                  {canvasNameById.get(a.canvas_id) ?? "Canvas"}
                </Link>
                <Link
                  to="/w/$slug/c/$canvasId"
                  params={{ slug: workspaceSlug ?? "", canvasId: a.canvas_id }}
                  className="shrink-0 text-[11px] text-[var(--color-brand-terracotta-deep)]"
                >
                  Open in Work mode →
                </Link>
                {mayEdit && (
                  <button
                    type="button"
                    className="shrink-0 rounded p-1 text-[var(--color-steel)] hover:bg-[var(--color-row-hover)] hover:text-[var(--color-semantic-error)]"
                    title="Detach canvas"
                    disabled={detach.isPending}
                    onClick={() => detach.mutate(a.id)}
                  >
                    <X className="size-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Artifacts — deliverables produced by this WP's canvases (below Canvases). */}
      <section className="section wp-section">
        <h2 className="sec">
          Artifacts{" "}
          <span className="cell-dim" style={{ fontWeight: 400 }}>
            ({artifacts.length})
          </span>
        </h2>
        {artifacts.length === 0 ? (
          <p className="sec-note">
            No artifacts yet — they appear here as this work package's canvases produce
            deliverables.
          </p>
        ) : (
          <div className="list">
            {artifacts.map((a, i) => {
              const ws = ART_STATE[a.status] ?? { label: "Draft", cls: "draft" };
              return (
                <div className="art-row" key={a.id}>
                  <span className="art-row-main">
                    <span className="ref-id">A-{i + 1}</span>
                    <span className="art-row-name">{a.title}</span>
                  </span>
                  <span className="art-row-type">{a.format.toUpperCase()}</span>
                  <span className={`art-status ${ws.cls}`}>{ws.label}</span>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {workspaceId && (
        <AttachCanvasPicker
          workspaceId={workspaceId}
          wpId={wpId}
          attachedCanvasIds={new Set(canvas_attachments.map((a) => a.canvas_id))}
          open={attachOpen}
          onOpenChange={setAttachOpen}
          onAttached={invalidate}
        />
      )}

      <LinkedIntegrations objectKind="work_package" objectId={wpId} canEdit={mayEdit} />

      {/* Activity feed — the WP's real event log (edits, completed tasks, tagged
          outputs incl. the Act→Plan bridge). Supporting context, last; collapsed
          by default with an Expand/Collapse toggle. */}
      <section className="section">
        <div className="wp-sec-head">
          <h2 className="sec" style={{ margin: 0 }}>
            Activity{" "}
            <span className="cell-dim" style={{ fontWeight: 400 }}>
              ({activity.length})
            </span>
          </h2>
          {activity.length > 0 && (
            <button className="wp-collapse" onClick={() => setActOpen((o) => !o)}>
              {actOpen ? "Collapse ▲" : "Expand ▼"}
            </button>
          )}
        </div>
        {activity.length === 0 ? (
          <p className="sec-note">No activity yet — edits and tagged outputs will appear here.</p>
        ) : !actOpen ? (
          <p className="sec-note">
            {activity.length} update{activity.length === 1 ? "" : "s"} hidden — expand to view the
            timeline.
          </p>
        ) : (
          <div className="feed">
            {activity.map((a) => {
              const who = a.actor_agent ?? a.actor_name ?? "Someone";
              const icon = ACT_ICON[a.type] ?? "•";
              return (
                <div className="feed-item" key={a.id}>
                  <span className={`act-ico t-${a.type === "output" ? "tag" : a.type}`}>{icon}</span>
                  <span className="feed-text">
                    <b>{who}</b> {a.verb}{" "}
                    {a.target ? <span className="feed-target">{a.target}</span> : null}
                  </span>
                  <span className="feed-at">{relAt(a.created_at)}</span>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </DeskScreen>
  );
}